import { FC, ReactNode, useCallback, useMemo } from 'react';
import { FeatureContext, FeatureFlags, FeatureId } from './helpers';
import useApp from '@/hooks/use-app.hook';
import useAppData from '@/hooks/use-app-data.hook';
import { AppDataQueryKey } from '@/lib/query-client';
import useQueryHelper from '@/hooks/use-query-helper';
import { userCan } from '@/lib/services/auth.service';
import { has } from 'lodash';
import { PartnerModel } from '@/lib/models/partner.model';
import { PartnerUserRole } from '@/lib/models/partner-user.model';

interface Props {
  children: ReactNode;
}

export const FeatureProvider: FC<Props> = ({ children }) => {
  const { user, signedIn } = useApp();

  const partnerBasicInfoQuery = useAppData<PartnerModel | null>(AppDataQueryKey.PartnerBasicInfo, {
    enabled: signedIn,
  });
  const { showLoader: partnerLoading } = useQueryHelper(partnerBasicInfoQuery);

  const canWrite = userCan(user, 'write');
  const isAuditor = user?.role === 'partner:auditor';
  const partnerUserRole: PartnerUserRole = user?.role ?? 'partner:auditor';

  const flags: FeatureFlags = useMemo(
    () => ({
      canChangeAuth: canWrite,
    }),
    [canWrite]
  );

  const hasFeature = useCallback(
    (featureFlagVal: string): boolean => {
      if (!has(flags, featureFlagVal)) {
        return false;
      }
      return !!flags[featureFlagVal];
    },
    [flags]
  );

  const getTooltip = useCallback(
    (feature: FeatureId) => {
      if (isAuditor) {
        return 'Adding, removing and modifying are disabled for the auditor role.';
      }
      switch (feature) {
        case 'change-auth':
          return 'Changing Single Sign-On and identity settings is not available.';
        default:
          return 'This feature is not available.';
      }
    },
    [isAuditor]
  );

  return (
    <FeatureContext.Provider
      value={{
        ...flags,
        partnerUserRole,
        getTooltip,
        hasFeature,
        loading: partnerLoading,
      }}
    >
      {children}
    </FeatureContext.Provider>
  );
};
